import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';

const ProductsTracking = () => {
    const { t } = useTranslation();
    const { authFetch, selectedDepo } = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    
    // Filters
    const [search, setSearch] = useState('');
    const [profitFilter, setProfitFilter] = useState('All'); // All, High, Normal, Loss
    const [stockFilter, setStockFilter] = useState('All'); // All, Healthy, Low
    const [showOnlySold, setShowOnlySold] = useState(false);
    const [dateRange, setDateRange] = useState('This Month');
    const [customStart, setCustomStart] = useState(new Date().toISOString().split('T')[0]);
    const [customEnd, setCustomEnd] = useState(new Date().toISOString().split('T')[0]);

    const getDateBounds = (range) => {
        const today = new Date();
        const y = today.getFullYear();
        const m = today.getMonth();
        const d = today.getDate();
        
        let start = new Date(today);
        
        if (range === 'Today') start = new Date(y, m, d);
        else if (range === 'This Week') {
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
            start = new Date(today.setDate(diff));
            start.setHours(0,0,0,0);
        }
        else if (range === 'This Month') start = new Date(y, m, 1);
        else if (range === 'All Time') start = new Date(2000, 0, 1);
        else if (range === 'Custom') {
            return { start_date: customStart, end_date: customEnd };
        }

        return {
            start_date: start.toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0]
        };
    };

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const { start_date, end_date } = getDateBounds(dateRange);
            const res = await authFetch(`/api/products_tracking?start_date=${start_date}&end_date=${end_date}&depo_id=${selectedDepo || 'Global'}`);
            if (res.ok) {
                const result = await res.json();
                const processed = result.map(item => {
                    const price = parseFloat(item.unit_price) || 0;
                    const cost = parseFloat(item.unit_cost) || 0;
                    const sqty = parseFloat(item.sold_qty) || 0;
                    const stkqty = parseFloat(item.stock_qty) || 0;
                    const sstock = parseFloat(item.safe_stock) || 0;

                    const unit_profit = price - cost;
                    const profit_margin = cost > 0 ? (unit_profit / cost) * 100 : (unit_profit > 0 ? 100 : 0);
                    const total_profit = unit_profit * sqty;
                    const is_low_stock = stkqty <= sstock;
                    const is_loss = unit_profit < 0;
                    
                    let profit_status = 'Normal';
                    if (is_loss) profit_status = 'Loss';
                    else if (profit_margin > 25) profit_status = 'High';

                    return {
                        ...item,
                        unit_price: price,
                        unit_cost: cost,
                        sold_qty: sqty,
                        stock_qty: stkqty,
                        unit_profit,
                        profit_margin,
                        total_profit,
                        is_low_stock,
                        is_loss,
                        profit_status
                    };
                });
                setData(processed);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange, customStart, customEnd, selectedDepo, authFetch]);

    // Computed filtered data
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const matchSearch = item.name.toLowerCase().includes(search.toLowerCase()) || item.sku.toLowerCase().includes(search.toLowerCase());
            const matchProfit = profitFilter === 'All' || item.profit_status === profitFilter;
            const matchStock = stockFilter === 'All' || (stockFilter === 'Low' ? item.is_low_stock : !item.is_low_stock);
            const matchSold = showOnlySold ? item.sold_qty > 0 : true;
            
            return matchSearch && matchProfit && matchStock && matchSold;
        });
    }, [data, search, profitFilter, stockFilter, showOnlySold]);

    // KPI Metrics
    const kpi = useMemo(() => {
        return {
            total_items: filteredData.length,
            sold_at_loss: filteredData.filter(i => i.is_loss).length,
            low_stock: filteredData.filter(i => i.is_low_stock).length,
            total_profit: filteredData.reduce((sum, i) => sum + i.total_profit, 0),
            avg_margin: filteredData.length > 0 
                ? (filteredData.reduce((sum, i) => sum + i.profit_margin, 0) / filteredData.length).toFixed(1) 
                : 0
        };
    }, [filteredData]);

    const formatCurr = (v) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0) + ' CFA';

    return (
        <div className="min-h-screen bg-slate-50/50 p-4 md:p-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                        <span className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">🎯</span>
                        {t('Products Tracking & Margins')}
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">
                        {t('Monitor sales margins, detect losses, and track low stock warnings in real-time.')}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <div className="flex bg-white rounded-xl shadow-sm border border-slate-200 p-1">
                        {['Today', 'This Week', 'This Month', 'All Time', 'Custom'].map(range => (
                            <button 
                                key={range}
                                onClick={() => setDateRange(range)}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${dateRange === range ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}
                            >
                                {t(range)}
                            </button>
                        ))}
                    </div>
                    {dateRange === 'Custom' && (
                        <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 px-3">
                            <span className="text-xs font-bold text-slate-400 uppercase">{t('From')}</span>
                            <input 
                                type="date" 
                                value={customStart} 
                                onChange={e => setCustomStart(e.target.value)} 
                                className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
                            />
                            <span className="text-slate-300 mx-1">→</span>
                            <span className="text-xs font-bold text-slate-400 uppercase">{t('To')}</span>
                            <input 
                                type="date" 
                                value={customEnd} 
                                onChange={e => setCustomEnd(e.target.value)} 
                                className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
                <div className="bg-white p-6 rounded-2xl border-l-4 border-indigo-500 shadow-sm flex flex-col justify-between">
                    <p className="text-slate-400 font-bold uppercase tracking-wider text-xs mb-1">{t('Total Tracked Products')}</p>
                    <h2 className="text-3xl font-black text-slate-800">{kpi.total_items}</h2>
                </div>
                
                <div className="bg-white p-6 rounded-2xl border-l-4 border-emerald-500 shadow-sm flex flex-col justify-between">
                    <p className="text-slate-400 font-bold uppercase tracking-wider text-xs mb-1">{t('Total Period Profit')}</p>
                    <h2 className="text-3xl font-black text-emerald-600">{formatCurr(kpi.total_profit)}</h2>
                    <p className="text-xs text-emerald-500 font-semibold mt-2">Avg Margin: {kpi.avg_margin}%</p>
                </div>

                <div className={`p-6 rounded-2xl border-l-4 shadow-sm flex flex-col justify-between transition-colors ${kpi.sold_at_loss > 0 ? 'bg-red-50 border-red-500' : 'bg-white border-slate-200'}`}>
                    <p className={`${kpi.sold_at_loss > 0 ? 'text-red-400' : 'text-slate-400'} font-bold uppercase tracking-wider text-xs mb-1`}>{t('Items Sold at Loss')}</p>
                    <div className="flex items-center gap-3">
                        <h2 className={`text-3xl font-black ${kpi.sold_at_loss > 0 ? 'text-red-700' : 'text-slate-800'}`}>{kpi.sold_at_loss}</h2>
                        {kpi.sold_at_loss > 0 && <span className="animate-pulse bg-red-600 text-white text-[10px] font-black px-2 py-1 rounded-full uppercase">Action Required</span>}
                    </div>
                </div>

                <div className={`p-6 rounded-2xl border-l-4 shadow-sm flex flex-col justify-between ${kpi.low_stock > 0 ? 'bg-orange-50 border-orange-500' : 'bg-white border-slate-200'}`}>
                    <p className={`${kpi.low_stock > 0 ? 'text-orange-500' : 'text-slate-400'} font-bold uppercase tracking-wider text-xs mb-1`}>{t('Low Stock Alerts')}</p>
                    <h2 className={`text-3xl font-black ${kpi.low_stock > 0 ? 'text-orange-700' : 'text-slate-800'}`}>{kpi.low_stock}</h2>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="relative w-full lg:w-96">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
                    <input 
                        type="text" 
                        placeholder={t('Search by Product Name or SKU...')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all"
                    />
                </div>
                
                <div className="flex flex-wrap gap-4 items-center w-full lg:w-auto">
                    {/* Toggle Switch */}
                    <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-xl transition-all hover:bg-slate-100">
                        <div className={`w-10 h-5 rounded-full relative transition-colors ${showOnlySold ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                            <div className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${showOnlySold ? 'translate-x-5' : 'translate-x-1'}`}></div>
                        </div>
                        <span className="text-sm font-bold text-slate-700 whitespace-nowrap">{t('Only Sold Items')}</span>
                        <input type="checkbox" className="hidden" checked={showOnlySold} onChange={(e) => setShowOnlySold(e.target.checked)} />
                    </label>

                    <div className="w-px h-8 bg-slate-200 hidden lg:block"></div>

                    <select 
                        value={profitFilter}
                        onChange={e => setProfitFilter(e.target.value)}
                        className="flex-1 lg:flex-none border border-slate-200 bg-slate-50 text-slate-700 text-sm font-bold rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                    >
                        <option value="All">📈 {t('All Profit Levels')}</option>
                        <option value="High">🚀 {t('High Profit (>25%)')}</option>
                        <option value="Normal">✓ {t('Normal Profit')}</option>
                        <option value="Loss">⚠️ {t('Sold at Loss')}</option>
                    </select>

                    <select 
                        value={stockFilter}
                        onChange={e => setStockFilter(e.target.value)}
                        className="flex-1 md:flex-none border border-slate-200 bg-slate-50 text-slate-700 text-sm font-bold rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                    >
                        <option value="All">📦 {t('All Stock Levels')}</option>
                        <option value="Healthy">✅ {t('Healthy Stock')}</option>
                        <option value="Low">🚨 {t('Low Stock Only')}</option>
                    </select>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead>
                            <tr className="bg-slate-50/80 text-slate-500 text-xs uppercase tracking-wider font-black border-b border-slate-200">
                                <th className="px-6 py-4">{t('Product & Stock')}</th>
                                <th className="px-6 py-4 text-right">{t('Sales (Qty)')}</th>
                                <th className="px-6 py-4 text-right">{t('Unit Cost')}</th>
                                <th className="px-6 py-4 text-right">{t('Sell Price')}</th>
                                <th className="px-6 py-4 text-center">{t('Margin %')}</th>
                                <th className="px-6 py-4 text-right">{t('Total Profit')}</th>
                                <th className="px-6 py-4 text-center">{t('Status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="inline-flex items-center gap-3 text-indigo-500 font-bold">
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {t('Analyzing Product Data...')}
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400 font-medium">
                                        📭 {t('No products match your current filters.')}
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map(item => (
                                    <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-800">{item.name}</span>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs font-mono text-slate-400">{item.sku}</span>
                                                    <span className="text-slate-300">•</span>
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md flex items-center gap-1 ${item.is_low_stock ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                        {item.is_low_stock ? '🚨 Low Stock' : '✅ Healthy'} 
                                                        ({item.stock_qty})
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        <td className="px-6 py-4 text-right">
                                            <span className="font-black text-slate-700">{item.sold_qty}</span>
                                        </td>
                                        
                                        <td className="px-6 py-4 text-right">
                                            <span className="font-semibold text-slate-500">{formatCurr(item.unit_cost)}</span>
                                        </td>
                                        
                                        <td className="px-6 py-4 text-right">
                                            <span className={`font-black ${item.is_loss ? 'text-red-600' : 'text-slate-800'}`}>
                                                {formatCurr(item.unit_price)}
                                            </span>
                                            {item.is_loss && <div className="text-[10px] text-red-500 font-bold uppercase mt-0.5">Below Cost</div>}
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-flex items-center justify-center px-2.5 py-1 rounded-lg text-xs font-black
                                                ${item.is_loss ? 'bg-red-100 text-red-700 border border-red-200' : 
                                                  item.profit_status === 'High' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 
                                                  'bg-blue-50 text-blue-700 border border-blue-100'}`}
                                            >
                                                {item.profit_margin > 0 ? '+' : ''}{item.profit_margin.toFixed(1)}%
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 text-right">
                                            <span className={`font-black text-base ${item.total_profit < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                {formatCurr(item.total_profit)}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            {item.is_loss ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-black shadow-sm shadow-red-500/30 ring-1 ring-red-700 animate-pulse">
                                                    ⚠️ SOLD AT LOSS
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 group-hover:text-slate-400 transition-colors">
                                                    —
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProductsTracking;
